// src/runtime/EventBus.ts
// ============================================
// EventBus - 运行时事件总线（冻结版本 v1.0）
// 
// 特性：
//   - Set 存储（天然防重复订阅）
//   - on() 返回取消订阅函数
//   - once() 一次性监听
//   - 支持通配符 (*)
//   - 支持同步/异步发送
//   - 支持优先级（High / Normal / Low）
//   - 支持按类型清理
//   - 事件快照（防止遍历时修改）
// ============================================

import { RuntimeEventType, RuntimeEvent, RuntimeEventListener } from '../types/runtime';

// ============================================
// Logger 接口（依赖注入）
// ============================================
export interface Logger {
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
}

// ============================================
// 监听器优先级
// ============================================
export type ListenerPriority = 'high' | 'normal' | 'low';

// ============================================
// 包装后的监听器（包含优先级）
// ============================================
interface WrappedListener {
  listener: RuntimeEventListener;
  priority: ListenerPriority;
}

// ============================================
// EventBus
// ============================================
export class EventBus {
  private listeners: Map<RuntimeEventType, Set<WrappedListener>> = new Map();
  private wildcardListeners: Set<WrappedListener> = new Set();
  private logger?: Logger;
  private eventHistory: RuntimeEvent[] = [];
  private maxHistory: number = 100;

  constructor(logger?: Logger) {
    this.logger = logger;
  }

  // ============================================
  // 依赖注入
  // ============================================
  setLogger(logger: Logger): void {
    this.logger = logger;
  }

  // ============================================
  // 1. 注册事件监听器（返回取消订阅函数）
  // ============================================
  on(
    type: RuntimeEventType | '*',
    listener: RuntimeEventListener,
    priority: ListenerPriority = 'normal'
  ): () => void {
    const wrapped: WrappedListener = { listener, priority };

    if (type === '*') {
      this.wildcardListeners.add(wrapped);
      this.logger?.debug(`[EventBus] Wildcard listener registered (priority: ${priority})`);
      return () => {
        this.wildcardListeners.delete(wrapped);
        this.logger?.debug('[EventBus] Wildcard listener removed');
      };
    }

    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }

    this.listeners.get(type)!.add(wrapped);
    this.logger?.debug(`[EventBus] Listener registered for ${type} (priority: ${priority})`);

    return () => {
      const set = this.listeners.get(type);
      if (set) {
        set.delete(wrapped);
        if (set.size === 0) {
          this.listeners.delete(type);
        }
        this.logger?.debug(`[EventBus] Listener removed for ${type}`);
      }
    };
  }

  // ============================================
  // 2. 一次性监听
  // ============================================
  once(
    type: RuntimeEventType,
    listener: RuntimeEventListener,
    priority: ListenerPriority = 'normal'
  ): () => void {
    const wrapped: RuntimeEventListener = (event: RuntimeEvent) => {
      // 先执行 listener，再取消订阅
      try {
        listener(event);
      } catch (error) {
        this.logger?.error('[EventBus] Once listener error:', error);
      }
      dispose();
    };

    const dispose = this.on(type, wrapped, priority);
    return dispose;
  }

  // ============================================
  // 3. 移除事件监听器
  // ============================================
  off(type: RuntimeEventType, listener: RuntimeEventListener): void {
    const set = this.listeners.get(type);
    if (!set) return;

    // 查找并删除匹配的监听器
    for (const wrapped of set) {
      if (wrapped.listener === listener) {
        set.delete(wrapped);
        break;
      }
    }

    if (set.size === 0) {
      this.listeners.delete(type);
    }

    this.logger?.debug(`[EventBus] Listener removed for ${type}`);
  }

  // ============================================
  // 4. 同步发送事件
  // ============================================
  emit(event: RuntimeEvent): void {
    const eventWithId = {
      ...event,
      id: (event as any).id || this.generateEventId(),
    };

    this.logger?.debug(`[EventBus] Emitting: ${eventWithId.type} from ${eventWithId.source}`);

    // 记录历史
    this.recordHistory(eventWithId);

    // 获取该类型的监听器快照（防止遍历时修改）
    const typeListeners = this.listeners.get(eventWithId.type);
    const typeSnapshot = typeListeners ? [...typeListeners] : [];

    // 获取通配符监听器快照
    const wildcardSnapshot = [...this.wildcardListeners];

    // 合并所有监听器
    const allListeners = [...typeSnapshot, ...wildcardSnapshot];

    // 按优先级排序（高 → 低）
    const priorityOrder: Record<ListenerPriority, number> = {
      high: 0,
      normal: 1,
      low: 2,
    };

    allListeners.sort((a, b) => {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    // 执行所有监听器
    for (const wrapped of allListeners) {
      try {
        wrapped.listener(eventWithId);
      } catch (error) {
        this.logger?.error('[EventBus] Listener error:', error);
      }
    }
  }

  // ============================================
  // 5. 异步发送事件
  // ============================================
  async emitAsync(event: RuntimeEvent): Promise<void> {
    const eventWithId = {
      ...event,
      id: (event as any).id || this.generateEventId(),
    };

    this.logger?.debug(`[EventBus] Emitting (async): ${eventWithId.type} from ${eventWithId.source}`);

    this.recordHistory(eventWithId);

    const typeListeners = this.listeners.get(eventWithId.type);
    const typeSnapshot = typeListeners ? [...typeListeners] : [];
    const wildcardSnapshot = [...this.wildcardListeners];
    const allListeners = [...typeSnapshot, ...wildcardSnapshot];

    const priorityOrder: Record<ListenerPriority, number> = {
      high: 0,
      normal: 1,
      low: 2,
    };

    allListeners.sort((a, b) => {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    // 异步执行（使用 queueMicrotask 让出主线程）
    const promises = allListeners.map((wrapped) => {
      return new Promise<void>((resolve) => {
        queueMicrotask(() => {
          try {
            wrapped.listener(eventWithId);
          } catch (error) {
            this.logger?.error('[EventBus] Async listener error:', error);
          }
          resolve();
        });
      });
    });

    await Promise.all(promises);
  }

  // ============================================
  // 6. 清理
  // ============================================
  clear(type?: RuntimeEventType): void {
    if (type) {
      this.listeners.delete(type);
      this.logger?.debug(`[EventBus] Cleared listeners for ${type}`);
    } else {
      this.listeners.clear();
      this.wildcardListeners.clear();
      this.eventHistory = [];
      this.logger?.debug('[EventBus] All listeners cleared');
    }
  }

  // ============================================
  // 7. 统计信息
  // ============================================
  listenerCount(type?: RuntimeEventType): number {
    if (type) {
      return this.listeners.get(type)?.size || 0;
    }
    let total = 0;
    for (const set of this.listeners.values()) {
      total += set.size;
    }
    total += this.wildcardListeners.size;
    return total;
  }

  getEventHistory(): RuntimeEvent[] {
    return [...this.eventHistory];
  }

  getEventHistoryFor(type: RuntimeEventType): RuntimeEvent[] {
    return this.eventHistory.filter((e) => e.type === type);
  }

  // ============================================
  // 8. 私有方法
  // ============================================
  private generateEventId(): string {
    return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private recordHistory(event: RuntimeEvent): void {
    this.eventHistory.push(event);
    if (this.eventHistory.length > this.maxHistory) {
      this.eventHistory.shift();
    }
  }

  // ============================================
  // 9. 重置（仅用于测试）
  // ============================================
  reset(): void {
    this.listeners.clear();
    this.wildcardListeners.clear();
    this.eventHistory = [];
    this.logger?.debug('[EventBus] Reset');
  }

  destroy(): void {
    this.listeners.clear();
    this.wildcardListeners.clear();
    this.eventHistory = [];
    this.logger = undefined;
  }
}

export default EventBus;